import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronUp, ChevronDown, X } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { scenarioStorage } from '@/lib/scenarioStorage';

export function PlanSummaryFloatingWidget() {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const scenarios = useMemo(() => scenarioStorage.getUserScenarios(), []);
  const totalCost = scenarios.reduce((sum, s) => sum + (s.estimatedCost || 0), 0);

  // Don't show if no scenarios or dismissed
  if (scenarios.length === 0 || dismissed) return null;

  return (
    <div
      className={`fixed right-6 z-[10010] rounded-xl shadow-2xl border transition-all ${
        isAhp
          ? 'bg-white border-gray-200'
          : 'bg-card border-foreground/10'
      } ${expanded ? 'w-72' : 'w-auto'}`}
      style={{ bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }}
    >
      {/* Header - always visible */}
      <div
        className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer rounded-xl ${
          isAhp ? 'hover:bg-gray-50' : 'hover:bg-muted/50'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
          isAhp ? 'bg-[#002868]/10' : 'bg-matrix-green/10'
        }`}>
          <Calendar className={`w-3.5 h-3.5 ${isAhp ? 'text-[#002868]' : 'text-matrix-green'}`} />
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
            My Plan
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
            isAhp ? 'bg-[#B22234] text-white' : 'bg-matrix-green text-black'
          }`}>
            {scenarios.length}
          </span>
          <span className={`text-xs font-mono font-bold ${
            isAhp ? 'text-[#002868]' : 'text-matrix-green'
          }`}>
            ${totalCost >= 1000 ? `${(totalCost / 1000).toFixed(totalCost >= 10000 ? 0 : 1)}k` : totalCost.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center ml-auto">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            aria-label={expanded ? 'Collapse plan summary' : 'Expand plan summary'}
            className="p-2 text-muted-foreground hover:text-foreground"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
            aria-label="Dismiss"
            className="p-2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className={`px-3 pb-3 border-t ${isAhp ? 'border-gray-100' : 'border-foreground/10'}`}>
          <div className="space-y-1 mt-2 max-h-[200px] overflow-y-auto">
            {scenarios.slice(0, 5).map(s => (
              <div key={s.id} className="flex items-center justify-between text-[11px] py-1">
                <span className={`truncate flex-1 mr-2 ${isAhp ? 'text-gray-700' : 'text-foreground/70'}`}>
                  {s.name}
                </span>
                <span className={`font-mono flex-shrink-0 ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
                  ${(s.estimatedCost || 0).toLocaleString()}
                </span>
              </div>
            ))}
            {scenarios.length > 5 && (
              <p className="text-[10px] text-muted-foreground">
                + {scenarios.length - 5} more
              </p>
            )}
          </div>
          <Link
            to="/family"
            className={`block mt-2 text-center text-xs font-semibold py-2 rounded-lg transition-colors ${
              isAhp
                ? 'bg-[#002868] text-white hover:bg-[#001d4e]'
                : 'bg-matrix-green text-black hover:bg-matrix-green/90'
            }`}
          >
            View Full Plan
          </Link>
        </div>
      )}
    </div>
  );
}
